import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import { GenericForm } from '@neo/blocks/forms/generic-form'
import type { GenericFormType } from '@neo/blocks/forms/types'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@neo/primitives/accordion'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { useEffect, useMemo, useRef, useState } from 'react'
import { REGIONS } from 'v2/configs/constants'
import { useAuth } from 'v2/shared/hooks/use-auth'
import type { AgentSettingsFormValues } from '../config/base.config'
import { useGetLanguages, useGetProviders } from '../knowledge-base/queries'
import { agentSettingsFields } from '../sidebar/agent-settings/agent-settings.config'
import { STTConfig } from '../sidebar/agent-settings/agent-settings.stt'
import { TTSConfig } from '../sidebar/agent-settings/agent-settings.tts'
import { UserIdleConfig } from '../sidebar/agent-settings/agent-settings-idle'
import { useFlowStore } from '../store'
import { getVoiceAiConfig, SUPPORTED_LANGUAGES } from '../utils/lang-utils'

export const VoiceConfigTab = () => {
  const { details } = useAuth()
  const region = details?.region || REGIONS.US
  const formRef = useRef<GenericFormType<AgentSettingsFormValues>>()
  const agentSettings = useFlowStore((state) => state.agentSettings)

  const { data: languagesData } = useGetLanguages((data) => ({
    ...data,
    data: {
      ...data.data,
      languages: data.data.languages.filter((lang) => SUPPORTED_LANGUAGES.includes(lang.code)),
    },
  }))

  const { data: providers } = useGetProviders()
  const [isFormReady, setIsFormReady] = useState(false)

  const selectedTtsProvider = useMemo(() => {
    if (!providers?.data || !region) return null
    const voiceConfig = getVoiceAiConfig([], region, providers.data)
    return providers.data.tts_providers?.find((provider) => provider.id === voiceConfig.ttsProvider)
  }, [providers?.data, region])

  const formFields = useMemo(() => {
    const languages = languagesData?.data?.languages || []
    return agentSettingsFields([], languages, providers?.data, region, [])
  }, [languagesData?.data?.languages, providers, region])

  useEffect(() => {
    queueMicrotask(() => {
      formRef.current?.validateAllFields('change')
    })
  }, [providers])

  const getVoiceSettingsFields = () =>
    formFields.filter(
      (field) => field.name.startsWith('voice_ai_config') && field.name !== 'knowledge_base_ids',
    )

  return (
    <div className="h-full">
      <ScrollArea className="h-[calc(100vh-200px)]">
        <div className="max-w-4xl overflow-visible pb-8 pr-4">
          {agentSettings && (
            <GenericForm<AgentSettingsFormValues>
              id="voice-config-tab-form"
              fields={[]}
              onMount={(form: GenericFormType<AgentSettingsFormValues>) => {
                formRef.current = form
                setIsFormReady(true)
                useFlowStore.getState().setTabFormInstance('voice-config', form)
                return () => useFlowStore.getState().setTabFormInstance('voice-config', null)
              }}
              onSubmit={() => {}}
              includeSubmitButton={false}
              defaultValues={agentSettings}
              className="gap-0"
            >
              <Accordion type="multiple" defaultValue={['voice-settings']} className="space-y-4">
                <AccordionItem
                  value="voice-settings"
                  className="overflow-visible rounded-lg border border-border bg-card px-4"
                >
                  <AccordionTrigger className="gap-4 rounded-md py-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25">
                    <div className="text-left">
                      <div className="text-h4-500 text-foreground">
                        Language, voice & pronunciations settings
                      </div>
                      <div className="mt-1 text-s-400 text-secondary">
                        Customise how the AI Agent speaks during a call. These apply only to call
                        conversation blocks.
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="flex flex-col gap-6 border-t border-border pt-5">
                      {isFormReady && formRef.current && (
                        <>
                          <FieldsRenderer
                            fields={getVoiceSettingsFields()}
                            form={formRef.current}
                          />
                          <TTSConfig
                            form={formRef.current}
                            voices={selectedTtsProvider?.voices || []}
                            providers={providers?.data}
                            region={region}
                          />
                        </>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value="transcription"
                  className="overflow-visible rounded-lg border border-border bg-card px-4"
                >
                  <AccordionTrigger className="gap-4 rounded-md py-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25">
                    <div className="text-left">
                      <div className="text-h4-500 text-foreground">
                        Agent speech transcription keyterms
                      </div>
                      <div className="mt-1 text-s-400 text-secondary">
                        Key terms the model should focus on during a call, like proper nouns or
                        product names.
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="border-t border-border pt-5">
                      {isFormReady && formRef.current && <STTConfig form={formRef.current} />}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem
                  value="idle-message"
                  className="overflow-visible rounded-lg border border-border bg-card px-4"
                >
                  <AccordionTrigger className="gap-4 rounded-md py-4 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/25">
                    <div className="text-left">
                      <div className="text-h4-500 text-foreground">User idle time message</div>
                      <div className="mt-1 text-s-400 text-secondary">
                        Customise how the AI will respond when user is not responding for a while.
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="border-t border-border pt-5">
                      {isFormReady && formRef.current && <UserIdleConfig form={formRef.current} />}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </GenericForm>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
